import type { Exercise, ExternalReference, UnknownExercise } from '@ilteducation/exercise-service-types';

/**
 * Columns the Exercises bulk import reads. Any other column in the sheet is ignored,
 * so divider columns and 'Book title (optional)' can stay where they are.
 * Header matching ignores case, spaces and punctuation: 'RequireLogin?' === 'Require login'.
 */
export const COLUMNS = {
  bookUid: 'Book UID',
  requireLogin: 'RequireLogin?',
  linkText: 'Link text',
  url: 'URL',
  title: 'Exercise title',
  author: 'Author',
  visibility: 'Visibility',
};

const REQUIRED = [COLUMNS.bookUid, COLUMNS.linkText, COLUMNS.url, COLUMNS.title];

export type ImportRow = Record<string, string>;

export type RowError = { row: number; message: string };

const normalize = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, '');

const cell = (row: ImportRow, column: string) => {
  const key = Object.keys(row).find((header) => normalize(header) === normalize(column));
  return key ? (row[key] ?? '').trim() : '';
};

// Anything not in this list is false, so blank / 'null' / 'undefined' all mean "no login needed".
const TRUTHY = ['true', 'yes', 'ja', '1', 'x'];

const parseLoginRequired = (value: string) => TRUTHY.includes(value.toLowerCase());

// The roles ExternalReference.vue offers. Anything else is a typo we should report, not import.
const VISIBILITY_ROLES = ['student', 'guardian', 'employee'];

// Sheets use commas, spaces or both: 'Guardian, Employee' and 'employee student' both mean two roles.
const parseVisibility = (value: string) =>
  value
    .split(/[\s,;/|]+/)
    .map((v) => v.toLowerCase())
    .filter(Boolean);

export const toExercise = (row: ImportRow): UnknownExercise => ({
  title: cell(row, COLUMNS.title),
  type: 'external-material',
  resourceId: cell(row, COLUMNS.bookUid),
  resourceType: 'book',
  author: { name: cell(row, COLUMNS.author) || 'ILT Education', type: 'alias' },
  content: [
    {
      type: 'external-reference',
      text: cell(row, COLUMNS.linkText),
      url: cell(row, COLUMNS.url),
      visibility: parseVisibility(cell(row, COLUMNS.visibility)),
      loginRequired: parseLoginRequired(cell(row, COLUMNS.requireLogin)),
    },
  ],
});

/** Splits the parsed CSV into exercises to send and rows to report back as unusable. */
export const parseRows = (rows: ImportRow[]) => {
  const exercises: UnknownExercise[] = [];
  const errors: RowError[] = [];

  rows.forEach((row, index) => {
    const problems = [];

    const missing = REQUIRED.filter((column) => !cell(row, column));
    if (missing.length) problems.push(`missing ${missing.join(', ')}`);

    const unknownRoles = parseVisibility(cell(row, COLUMNS.visibility)).filter(
      (role) => !VISIBILITY_ROLES.includes(role),
    );
    if (unknownRoles.length) problems.push(`unknown ${COLUMNS.visibility}: ${unknownRoles.join(', ')}`);

    if (problems.length) {
      // +2: header line, plus 1-based line numbering, so it matches what the spreadsheet shows
      errors.push({ row: index + 2, message: problems.join('; ') });
      return;
    }
    exercises.push(toExercise(row));
  });

  return { exercises, errors };
};

const referenceUrl = (exercise: UnknownExercise) =>
  (exercise.content.find((c) => c.type === 'external-reference') as ExternalReference | undefined)?.url;

/**
 * An imported exercise is "the same one" as an existing one when it points at the same book
 * with the same URL. Title, link text, visibility and login flag are all editable via re-import.
 */
export const findExisting = (existing: Exercise[], exercise: UnknownExercise) =>
  existing.find((e) => e.resourceId === exercise.resourceId && referenceUrl(e) === referenceUrl(exercise));

export const chunk = <T>(items: T[], size: number): T[][] =>
  items.reduce<T[][]>((acc, item, index) => {
    if (index % size === 0) acc.push([]);
    acc[acc.length - 1].push(item);
    return acc;
  }, []);
